import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, Select, Button } from '../components/ui';
import { AiInsightsModal, type AiResult } from '../components/AiInsightsModal';
import { useApi } from '../lib/hooks';
import { api } from '../lib/api';
import type { ApiResponse } from '../lib/types';

interface ReportRow {
  campaign_uuid?: string;
  campaign_name: string;
  client_name?: string;
  clicks: number;
  pageviews: number;
  cost: number;            // compat: mesmo valor de revenue
  revenue?: number;        // faturado ao cliente (antigo "cost")
  server_cost?: number | null; // rateio do custo AWS por pageview
  profit?: number | null;  // revenue - server_cost
  margin?: number | null;  // profit / revenue (%)
  unique_users?: number | null; // Athena (ALB logs), via cache
}
interface UniqueRow { campaign_id: string; unique_users: number | null; updated_at?: string }
interface ClientOpt { id: number; name: string }

const now = new Date();
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const num = (v: unknown) => Number(v) || 0;
const usd = (v: number) => `$${v.toFixed(2)}`;

type SortKey = 'pageviews' | 'clicks' | 'revenue' | 'server_cost' | 'profit' | 'margin' | 'unique_users';

export default function Reports() {
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');

  useEffect(() => {
    api.get<ApiResponse<ClientOpt[]>>('/clients/list').then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  const url = `/reports/data?month=${month}&year=${year}` + (clientId ? `&client_id=${clientId}` : '');
  const { data, loading, error } = useApi<ReportRow[]>(url);

  // Unique users (Athena/ALB logs) — cache atualizado 1x/dia (~05h BRT) ou sob demanda
  const [uniques, setUniques] = useState<Map<string, number | null>>(new Map());
  const [uniquesUpdatedAt, setUniquesUpdatedAt] = useState<string | null>(null);
  const [uniquesBusy, setUniquesBusy] = useState(false);
  function loadUniques() {
    api.get<ApiResponse<UniqueRow[]>>(`/reports/uniques?month=${month}&year=${year}`)
      .then((r) => {
        const m = new Map<string, number | null>();
        let latest: string | null = null;
        for (const u of r.data || []) {
          m.set(u.campaign_id, u.unique_users);
          if (u.updated_at && (!latest || u.updated_at > latest)) latest = u.updated_at;
        }
        setUniques(m);
        setUniquesUpdatedAt(latest);
      })
      .catch(() => {});
  }
  useEffect(() => { loadUniques(); }, [month, year]); // eslint-disable-line react-hooks/exhaustive-deps
  async function refreshUniques() {
    setUniquesBusy(true);
    try {
      await api.post('/reports/uniques/refresh', { month, year });
      loadUniques();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to refresh unique users');
    } finally {
      setUniquesBusy(false);
    }
  }

  const rows = useMemo(() => {
    const arr = (data || []).map((r) => ({
      ...r,
      unique_users: r.campaign_uuid != null ? (uniques.get(r.campaign_uuid) ?? null) : null,
    }));
    if (sortKey) {
      const dir = sortDir === 'desc' ? 1 : -1;
      arr.sort((a, b) => (num(b[sortKey] ?? (sortKey === 'revenue' ? b.cost : 0)) - num(a[sortKey] ?? (sortKey === 'revenue' ? a.cost : 0))) * dir);
    }
    return arr;
  }, [data, uniques, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(k); setSortDir('desc'); }
  }
  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === 'desc' ? ' ▾' : ' ▴') : '');

  function Th({ k, children }: { k: SortKey; children: React.ReactNode }) {
    return (
      <th className="px-4 py-3 font-medium text-right cursor-pointer select-none hover:text-ink-900" onClick={() => toggleSort(k)} title="Click to sort">
        {children}{arrow(k)}
      </th>
    );
  }

  // Análise por IA do mês na tela (mesmo modelo/config das outras análises)
  const [aiResult, setAiResult] = useState<AiResult | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  async function analyzeReport() {
    if (!rows.length) return;
    setAiBusy(true);
    try {
      const r = await api.post<ApiResponse<AiResult>>('/reports/analyze', {
        month, year,
        rows: rows.map((row) => ({
          campaign: row.campaign_name,
          client: row.client_name || null,
          pageviews: num(row.pageviews),
          unique_users: row.unique_users ?? null,
          clicks: num(row.clicks),
          revenue: num(row.revenue ?? row.cost),
          server_cost: row.server_cost ?? null,
          profit: row.profit ?? null,
          margin: row.margin ?? null,
        })),
        totals: {
          pageviews: totalPageviews, clicks: totalClicks, revenue: totalRevenue,
          server_cost: hasServerCost ? totalServerCost : null,
          profit: hasServerCost ? totalProfit : null,
        },
      });
      if (!r.success || !r.data) throw new Error(r.error || r.message || 'Failed');
      setAiResult(r.data);
      setAiOpen(true);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to analyze');
    } finally {
      setAiBusy(false);
    }
  }

  const revenueOf = (r: ReportRow) => num(r.revenue ?? r.cost);
  const totalClicks = rows.reduce((a, r) => a + num(r.clicks), 0);
  const totalPageviews = rows.reduce((a, r) => a + num(r.pageviews), 0);
  const totalRevenue = rows.reduce((a, r) => a + revenueOf(r), 0);
  const hasServerCost = rows.some((r) => r.server_cost != null);
  const totalServerCost = rows.reduce((a, r) => a + num(r.server_cost), 0);
  const totalProfit = totalRevenue - totalServerCost;

  return (
    <Layout
      title="Reports"
      actions={
        <div className="flex gap-2">
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-44">
            <option value="">All Clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="w-28">
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </Select>
          <Select value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24">
            {[year, year - 1, year - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
          <Button className="bg-violet-600 hover:bg-violet-700 text-white whitespace-nowrap" disabled={aiBusy || loading || rows.length === 0}
            onClick={analyzeReport} title="Analisa com IA o report do mês na tela (margens, custos, concentração de receita)">
            {aiBusy ? '…' : '✨ AI'}
          </Button>
        </div>
      }
    >
      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error}</div>}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
        <Card className="p-5"><div className="text-2xl font-bold text-ink-900">{totalPageviews.toLocaleString()}</div><div className="text-sm text-slate-500">Total Pageviews</div></Card>
        <Card className="p-5"><div className="text-2xl font-bold text-ink-900">{totalClicks.toLocaleString()}</div><div className="text-sm text-slate-500">Total Clicks</div></Card>
        <Card className="p-5"><div className="text-2xl font-bold text-ink-900">{usd(totalRevenue)}</div><div className="text-sm text-slate-500">Total Revenue</div></Card>
        <Card className="p-5"><div className="text-2xl font-bold text-ink-900">{hasServerCost ? usd(totalServerCost) : '—'}</div><div className="text-sm text-slate-500">Total Cost (AWS)</div></Card>
        <Card className="p-5">
          <div className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{hasServerCost ? usd(totalProfit) : '—'}</div>
          <div className="text-sm text-slate-500">Total Profit{hasServerCost && totalRevenue > 0 ? ` · ${((totalProfit / totalRevenue) * 100).toFixed(1)}% margin` : ''}</div>
        </Card>
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
                <th className="px-4 py-3 font-medium">Campaign</th>
                <th className="px-4 py-3 font-medium">Client</th>
                <Th k="pageviews">Pageviews</Th>
                <Th k="unique_users">Unique Users</Th>
                <Th k="clicks">Clicks</Th>
                <Th k="revenue">Revenue</Th>
                <Th k="server_cost">Cost</Th>
                <Th k="profit">Profit</Th>
                <Th k="margin">Margin</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && <tr><td colSpan={9} className="px-4 py-6 text-slate-400">Loading…</td></tr>}
              {!loading && rows.map((r, i) => {
                const revenue = revenueOf(r);
                const profit = r.profit;
                return (
                  <tr key={i} className="hover:bg-surface-muted/60">
                    <td className="px-4 py-3 font-medium">
                      {r.campaign_uuid ? (
                        <Link to={`/campaigns/view/${r.campaign_uuid}`} className="text-ink-900 hover:text-brand-600 hover:underline">{r.campaign_name}</Link>
                      ) : (
                        <span className="text-ink-900">{r.campaign_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{r.client_name || '—'}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{num(r.pageviews).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.unique_users != null ? num(r.unique_users).toLocaleString() : '—'}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{num(r.clicks).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{usd(revenue)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.server_cost != null ? usd(num(r.server_cost)) : '—'}</td>
                    <td className={`px-4 py-3 text-right font-medium ${profit == null ? 'text-slate-400' : num(profit) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{profit != null ? usd(num(profit)) : '—'}</td>
                    <td className={`px-4 py-3 text-right ${r.margin == null ? 'text-slate-400' : num(r.margin) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{r.margin != null ? `${num(r.margin).toFixed(1)}%` : '—'}</td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No data in this period.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
        <p className="text-[11px] text-slate-400">
          Revenue = amount billed to the client (CPC). Cost = AWS server cost for the month apportioned to each campaign by its share of total pageviews. Profit = Revenue − Cost.
          <br />Unique Users = distinct signal IDs in the Audience ALB logs (Athena), refreshed daily at ~5am BRT{uniquesUpdatedAt ? ` · last update ${new Date(uniquesUpdatedAt).toLocaleString()}` : ' · no data yet'}.
        </p>
        <button onClick={refreshUniques} disabled={uniquesBusy}
          className="text-xs rounded-lg border border-border px-3 py-1.5 text-slate-600 hover:bg-surface-muted disabled:opacity-50">
          {uniquesBusy ? 'Running Athena… (~20s)' : '↻ Refresh unique users now'}
        </button>
      </div>

      <AiInsightsModal open={aiOpen} onClose={() => setAiOpen(false)} title={`✨ Report Insights (${MONTHS[month - 1]}/${year})`} result={aiResult} />
    </Layout>
  );
}
